import { assertNever } from 'owa-assert';
import type { OwaDate } from 'owa-datetime';
import {
    addDays,
    endOfWeek,
    getDay,
    getISOString,
    now,
    startOfDay,
    startOfWeek,
} from 'owa-datetime';
import { createWorkWeekDaysArray, CalendarConstants } from 'owa-datetime-utils';
import type FlagStatus from 'owa-service/lib/contract/FlagStatus';
import type FlagType from 'owa-service/lib/contract/FlagType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { isCapabilityEnabled } from 'owa-capabilities';
import { advancedFlagCapability } from 'owa-capabilities-definitions/lib/advancedFlagCapability';

export enum FlagCompleteEnum {
    today,
    tomorrow,
    thisWeek,
    nextWeek,
    noDate,
    complete,
    clear,
    custom,
}

export default function getFlagCompleteState(
    flagAction: FlagCompleteEnum,
    customStartDate?: OwaDate,
    customDueDate?: OwaDate,
    reminderIsSet?: boolean,
    reminderTime?: OwaDate,
    reminderNextTime?: OwaDate
): FlagType {
    const userOptions = getUserConfiguration().UserOptions;
    const workDays = userOptions?.WorkingHours?.WorkDays ?? CalendarConstants.DefaultWorkDays;

    let dueDate: string | undefined;
    let startDate: string | undefined;
    let completeDate: string | undefined;
    let reminderTimeToApply: string | undefined;
    let reminderNextTimeToApply: string | undefined;

    let dates: string[];
    let flagStatusToApply: FlagStatus = 'NotFlagged';
    const currentTime = now();

    switch (flagAction) {
        case FlagCompleteEnum.today:
            startDate = getISOString(currentTime);
            dueDate = startDate;
            flagStatusToApply = 'Flagged';
            break;
        case FlagCompleteEnum.tomorrow:
            startDate = getISOString(addDays(startOfDay(currentTime), 1));
            dueDate = startDate;
            flagStatusToApply = 'Flagged';
            break;
        case FlagCompleteEnum.thisWeek:
            dates = setFlagThisWeek(workDays, currentTime);
            startDate = dates[0];
            dueDate = dates[1];
            flagStatusToApply = 'Flagged';
            break;
        case FlagCompleteEnum.nextWeek:
            dates = setFlagNextWeek(
                userOptions?.WeekStartDay ?? CalendarConstants.DefaultWeekStartDay,
                workDays,
                currentTime
            );
            startDate = dates[0];
            dueDate = dates[1];
            flagStatusToApply = 'Flagged';
            break;
        case FlagCompleteEnum.noDate:
            flagStatusToApply = 'Flagged';
            break;
        case FlagCompleteEnum.complete:
            completeDate = getISOString(currentTime);
            flagStatusToApply = 'Complete';
            break;
        case FlagCompleteEnum.clear:
            flagStatusToApply = 'NotFlagged';
            break;
        case FlagCompleteEnum.custom:
            if (
                isCapabilityEnabled(advancedFlagCapability) &&
                !!customStartDate &&
                !!customDueDate
            ) {
                flagStatusToApply = 'Flagged';
                startDate = getISOString(customStartDate);
                dueDate = getISOString(customDueDate);
                reminderTimeToApply = reminderTime ? getISOString(reminderTime) : undefined;
                reminderNextTimeToApply = reminderNextTime
                    ? getISOString(reminderNextTime)
                    : undefined;
            }
            break;
        default:
            assertNever(flagAction);
    }

    const flagType: FlagType = {
        FlagStatus: flagStatusToApply,
        StartDate: startDate,
        DueDate: dueDate,
        CompleteDate: completeDate,
        ReminderIsSet: reminderIsSet,
        ReminderTime: reminderTimeToApply,
        ReminderNextTime: reminderNextTimeToApply,
    };
    return flagType;
}

export function setFlagThisWeek(workDaysNum: number, currentTime: OwaDate): [string, string] {
    const dayOfWeek = getDay(currentTime);
    const daysToStartOfNextWeek = 7 - dayOfWeek;
    let daysToStartDate = 2;

    // If days to end of week is <= 2 && > 0, then we need to back up so that we are within this week
    if (0 < daysToStartOfNextWeek && daysToStartOfNextWeek <= 2) {
        daysToStartDate = daysToStartOfNextWeek - 1;
    }

    // Calculate start date
    let startDate = addDays(startOfDay(currentTime), daysToStartDate);

    const workDays = createWorkWeekDaysArray(workDaysNum);

    // Make sure start date is a working day, if not, move backwards to the last working day,
    // or until we get to todays date
    while (currentTime < startDate && !workDays.includes(getDay(startDate))) {
        startDate = addDays(startOfDay(startDate), -1);
    }

    // Set due date to the end of this week
    let dueDate = addDays(startOfDay(currentTime), daysToStartOfNextWeek - 1);

    // Check if the due date is a working day, if it isn't, go back until we hit a working day or
    // until we get to start date
    while (startDate < dueDate && !workDays.includes(getDay(dueDate))) {
        dueDate = addDays(startOfDay(dueDate), -1);
    }
    return [getISOString(startDate), getISOString(dueDate)];
}

export function setFlagNextWeek(
    weekStartDay: number,
    workDaysNum: number,
    currentTime: OwaDate
): [string, string] {
    const nextWeekToday = addDays(currentTime, 7);

    let startDate = startOfWeek(nextWeekToday, weekStartDay);

    const workDays = createWorkWeekDaysArray(workDaysNum);

    // Check if the start date is a working day, if it isn't, go forward until we hit a working day or until
    // we hit the end of next week at which point we leave it on that date
    for (let i = 0; i < 7 && !workDays.includes(getDay(startDate)); ++i) {
        startDate = addDays(startOfDay(startDate), 1);
    }

    let dueDate = endOfWeek(nextWeekToday, weekStartDay);
    while (startDate < dueDate && !workDays.includes(getDay(dueDate))) {
        dueDate = addDays(startOfDay(dueDate), -1);
    }
    return [getISOString(startDate), getISOString(dueDate)];
}
